import { toast } from '../../../components/ui/toaster';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import type { SecurityManagerTabContent } from '../../workspace/core/types/workspaceTypes';
import {
  getConnectionContext,
  mapExplorerNodeTypeToSecurityManagerType,
  type SecurityExplorerNodeContext,
} from './securityExplorerNodeContext';

export function openSecurityPropertiesFromExplorerNode(context: SecurityExplorerNodeContext): void {
  const mapping = mapExplorerNodeTypeToSecurityManagerType(context.nodeType);
  if (!mapping) {
    toast.error({ title: 'Unsupported', description: `No Properties view for ${context.nodeType}` });
    return;
  }

  const connectionContext = getConnectionContext(context.connectionId);
  if (!connectionContext) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  if (mapping.view === 'database' && !context.database) {
    toast.error({ title: 'Error', description: 'Database is required for this action' });
    return;
  }

  const { tabs, addTab, setActiveTab, updateTabContent, updateTabConnectionInfo, updateTabTitle } =
    useWorkspaceStore.getState();

  const content: Partial<SecurityManagerTabContent> = {
    view: mapping.view,
    objectType: mapping.objectType,
    selectedDatabase: mapping.view === 'database' ? context.database : undefined,
    selectedObjectId: context.principalId,
    isCreateMode: false,
  };

  const connectionLabel =
    connectionContext.connectionName === 'Unnamed Connection'
      ? connectionContext.serverHost
      : connectionContext.connectionName;

  const title = `Security Manager - ${connectionLabel}`;
  const connectionInfo = {
    connectionId: context.connectionId,
    connectionName: connectionContext.connectionName,
    serverHost: connectionContext.serverHost,
    database: context.database ?? connectionContext.defaultDatabase,
  };

  const existingTab = tabs.find(
    (tab) => tab.type === 'security' && tab.connectionInfo?.connectionId === context.connectionId
  );

  if (existingTab) {
    updateTabContent(existingTab.id, content);
    updateTabConnectionInfo(existingTab.id, connectionInfo);
    if (existingTab.customTitle !== true) {
      updateTabTitle(existingTab.id, title);
    }
    setActiveTab(existingTab.id);
    return;
  }

  addTab({ type: 'security', title, connectionInfo, content });
}
